import { h, Fragment } from 'preact';
import { useState, useRef, useEffect } from 'preact/hooks';
import {
  formatMoney,
  formatCartLinesAjax,
} from 'utils/storefront/format-resource';
import CloseIcon from 'utils/icon/icon-close.svg';
import clsx from 'clsx';
import { Transition } from '@headlessui/react';
import { withCartContext } from './cart-context';
import CartItem from './cart-item';
import CartThreshold from './cart-threshold';
import CartEmpty from './cart-empty';
import CartUpSell from './cart-upsell';
import CartUpSellPairsWith from './cart-upsell-pairs-with';
import CartUpSellBrRec from './cart-upsell-br-recommendation';
import CartBlock from './cart-block';
import RewardBalance from '../header/reward-balance';
import CartLabelSingleton from './cart-label';

const Cart = (props) => {
  const { cart, cartLoading } = props;
  const {
    blocks,
    threshold,
    emptyCart,
    upSell,
    upSellAbTest,
    dynamicCheckout,
    showRewardsBalance,
  } = props.data;
  const [isOpen, setIsOpen] = useState(false);
  const [showPromo, setShowPromo] = useState(true);
  const checkoutButtons = useRef(null);
  const scrollOffset = useRef(0);

  useEffect(() => {
    if (dynamicCheckout) {
      const buttonsContainer = document.getElementById('checkout-buttons');
      if (buttonsContainer) {
        checkoutButtons.current?.appendChild(buttonsContainer);
      }
    }

    if (new URLSearchParams(window.location.search).get('cart')) {
      setIsOpen(true);
    }
    const openCart = () => setIsOpen(true);
    document.addEventListener('openMiniCart', openCart);
    return () => {
      document.removeEventListener('openMiniCart', openCart);
    };
  }, []);

  useEffect(() => {
    if (isOpen) {
      document.body.classList.add('disabled-scroll-cart');
      const { scrollY } = window;
      scrollOffset.current = scrollY;
      if (scrollY === 0 && document.body.style.marginTop) {
        scrollOffset.current = Math.abs(
          parseInt(document.body.style.marginTop, 10),
        );
        document.body.style.top = document.body.style.marginTop;
      } else {
        document.body.style.top = `-${scrollY}px`;
      }

      // Refresh cart labels when mini cart opens
      // eslint-disable-next-line no-console
      console.log('Cart.js: Mini cart opened, triggering label refresh');

      // Use a small delay to ensure the cart is fully loaded before refreshing labels
      setTimeout(() => {
        CartLabelSingleton.refresh('minicart').catch((error) => {
          // eslint-disable-next-line no-console
          console.error(
            'Error refreshing cart labels on mini cart open:',
            error,
          );
        });
      }, 100);
    } else {
      document.body.classList.remove('disabled-scroll-cart');
      document.body.style.top = `0`;
    }
  }, [isOpen]);

  const itemsCount =
    window.TMW.Strings.cart.itemsCount[
      cart.state.item_count === 1 ? 'one' : 'other'
    ];

  const lines =
    cart?.mode === 'storefront'
      ? cart?.state?.lines
      : formatCartLinesAjax(cart?.state?.items);

  // find last added item using _cart_order property value
  const lastItem = lines.reduce((maxItem, currentItem) => {
    const currentOrder = currentItem.properties?._cart_order || 0;
    const maxOrder = maxItem.properties?._cart_order || 0;
    return currentOrder > maxOrder ? currentItem : maxItem;
  }, lines[0] || {});

  // find details.pairs_with value of last added item using cart.productsData
  let lastItemPairsWithData = null;
  Object.values(cart.productsData).forEach((product) => {
    if (product.masterSKU?.value === lastItem.vendor) {
      lastItemPairsWithData = product.pairsWith;
    }
  });

  // show Bloomreach recommendation if last added item has no detail.pairswith data
  const showBrRecommendation = !lastItemPairsWithData;

  // Handle checkout with cart label refresh
  const handleCheckout = async (e) => {
    e.preventDefault();

    try {
      // eslint-disable-next-line no-console
      console.log('Cart.js: Checkout initiated, triggering label refresh');

      // Force refresh cart labels before checkout (critical path)
      // Using forceRefresh to bypass cooldown and ensure labels are current
      await CartLabelSingleton.forceRefresh('checkout');

      // eslint-disable-next-line no-console
      console.log('Cart.js: Label refresh completed, proceeding to checkout');

      // Proceed to checkout after refresh completes
      const checkoutUrl =
        cart.mode === 'storefront' ? cart?.state?.checkoutUrl : '/checkout';

      window.location.href = checkoutUrl;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error refreshing cart labels before checkout:', error);

      // Still proceed to checkout even if refresh fails
      const checkoutUrl =
        cart.mode === 'storefront' ? cart?.state?.checkoutUrl : '/checkout';

      // eslint-disable-next-line no-console
      console.log(
        'Cart.js: Proceeding to checkout despite label refresh error',
      );
      window.location.href = checkoutUrl;
    }
  };

  return (
    <>
      <div
        className={clsx(
          'mini-cart fixed top-0 right-0 bottom-0 w-full md:w-[507px] z-[1003] bg-white ease-in duration-300',
          {
            'translate-x-full invisible': !isOpen,
          },
        )}
      >
        <div className="flex flex-col h-full below-320:overflow-y-auto">
          <div
            className="flex gap-10 py-16 px-24 tablet:py-24"
            id="mini-cart-header"
          >
            <div className="flex-1 heading-3">
              {window.TMW.Strings.cart.yourBag}
              {cart.state.item_count > 0 &&
                ` (${cart.state.item_count} ${itemsCount})`}
            </div>
            <button
              type="button"
              aria-label={window.TMW.Strings.cart.close}
              onClick={() => setIsOpen(false)}
            >
              <CloseIcon className="h-24 w-auto" />
            </button>
          </div>
          {cartLoading && (
            <div className="flex-1 animate-pulse lc:py-32 px-24 ml:py-40 text-center">
              {window.TMW.Strings.storeLocator.loading}
            </div>
          )}
          <>
            {cart.state.item_count === 0 && !cartLoading && (
              <>
                <div className="flex-1 min-h-0 overflow-y-auto overscroll-none">
                  {showRewardsBalance && (
                    <RewardBalance
                      showInCart={true}
                      cartIsEmpty={true}
                      onChange={(balance) =>
                        setShowPromo(!(balance?.amount > 0))
                      }
                    />
                  )}
                  <CartEmpty {...emptyCart} />
                  {!!upSell?.length &&
                    upSell.map((item, index) =>
                      item?.state === 'empty' ? (
                        <CartUpSell
                          upSellData={item}
                          cartEmpty={true}
                          key={index}
                          index={index}
                          isOpen={isOpen}
                          itemCount={cart.state.item_count}
                        />
                      ) : null,
                    )}
                </div>
              </>
            )}
            <div
              id="mini-cart-items"
              className={clsx('flex-1 min-h-0 flex flex-col', {
                hidden: cart.state.item_count < 1 || cartLoading,
              })}
            >
              <div className="flex-1 over-320:min-h-0 over-320:overflow-y-auto over-320:overscroll-none px-24">
                <CartThreshold {...threshold} />
                <div className="display-5 ml:text-center ml:py-8">
                  {window.TMW.Strings.cart.items}
                </div>
                <div>
                  {lines.map((line) => (
                    <CartItem key={line.id} line={line} />
                  ))}
                </div>

                {!!upSell?.length &&
                  upSell.map((item, index) =>
                    item?.state !== 'empty' ? (
                      <CartUpSell
                        upSellData={item}
                        cartEmpty={false}
                        key={index}
                        index={index}
                        isOpen={isOpen}
                        itemCount={cart.state.item_count}
                        lines={lines}
                        itemsData={cart.productsData}
                      />
                    ) : null,
                  )}

                {!!upSellAbTest && !showBrRecommendation && (
                  <CartUpSellPairsWith
                    upSellData={upSellAbTest}
                    key="CartUpSellPairsWith"
                    index="pairs-with"
                    isOpen={isOpen}
                    lastItem={lastItem}
                    lastItemPairsWithData={lastItemPairsWithData}
                  />
                )}

                {!!upSellAbTest && showBrRecommendation && (
                  <CartUpSellBrRec
                    upSellData={upSellAbTest}
                    key="CartUpSellBrRec"
                    index="br-recommendation"
                    isOpen={isOpen}
                    lastItem={lastItem}
                    lastItemPairsWithData={lastItemPairsWithData}
                  />
                )}
              </div>
              {!!blocks.promo?.settings.text.length && showPromo && (
                <div
                  className="lc:bg-accent-1 ml:bg-accent-2 py-12 px-16 body-3 text-center"
                  data-target="text-content"
                  dangerouslySetInnerHTML={{
                    __html: blocks.promo.settings.text,
                  }}
                ></div>
              )}
              {showRewardsBalance && (
                <RewardBalance
                  showInCart={true}
                  onChange={(balance) => setShowPromo(!(balance?.amount > 0))}
                />
              )}
              <div className="py-12 px-16" id="mini-cart-cta-section">
                {cart.state.total_price > 0 && (
                  <div className="body-2 text-center">
                    {window.TMW.Strings.cart.subtotal}
                    {` (${cart.state.item_count} ${itemsCount}): `}
                    <b className="font-semibold">
                      {formatMoney(
                        cart?.state?.total_price
                          ? { amount: cart.state.total_price }
                          : { amount: 0 },
                        true,
                        true,
                      )}
                    </b>
                  </div>
                )}
                <div className="py-12">
                  <a
                    className="justify-center w-full button button--primary checkout-cta"
                    href="/checkout"
                    onClick={handleCheckout}
                  >
                    {window.TMW.Strings.cart.checkout}
                  </a>
                </div>
                {dynamicCheckout && (
                  <div ref={checkoutButtons} className="checkout-buttons"></div>
                )}
                <div className="flex flex-col gap-4">
                  {Object.keys(blocks)
                    .filter((key) => key !== 'promo')
                    .map((key) => (
                      <CartBlock
                        key={key}
                        {...blocks[key]}
                        total={cart.state.total_price}
                      />
                    ))}
                </div>
              </div>
            </div>
          </>
        </div>
      </div>
      <Transition
        show={isOpen}
        as={Fragment}
        enter="transition-opacity ease-linear duration-300"
        enterFrom="opacity-0"
        enterTo="opacity-100"
        leave="transition-opacity ease-linear duration-300"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <div
          className="fixed inset-0 bg-black/20 z-[1002]"
          aria-hidden="true"
          onClick={() => setIsOpen(false)}
        />
      </Transition>
    </>
  );
};

export default withCartContext(Cart);
